import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useWorkObjects, type WorkObject } from '../../hooks/useWorkObjects';
import { ChevronLeft, ChevronRight, Calendar, Clock, User, Tag } from 'lucide-react';

interface CalendarEvent {
  id: string;
  title: string;
  date: Date;
  workObject: WorkObject;
  color: string;
}

export const WorkCalendar: React.FC = () => {
  const { data, loading } = useWorkObjects();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  const calendarEvents = useMemo(() => {
    return data.items
      .filter(item => item.dueAt || item.startAt)
      .map(item => ({
        id: item.id,
        title: item.title,
        date: new Date(item.dueAt || item.startAt || ''),
        workObject: item,
        color: getStatusColor(item.status)
      }));
  }, [data.items]);

  const getStatusColor = (status: WorkObject['status']) => {
    switch (status) {
      case 'Backlog': return 'from-slate-500 to-slate-600';
      case 'In Progress': return 'from-blue-500 to-blue-600';
      case 'Blocked': return 'from-red-500 to-red-600';
      case 'Review': return 'from-yellow-500 to-yellow-600';
      case 'Done': return 'from-green-500 to-green-600';
      default: return 'from-purple-500 to-purple-600';
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDate = (date: Date | null) => {
    if (!date) return [];
    return calendarEvents.filter(event => 
      event.date.toDateString() === date.toDateString()
    );
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gradient-to-br from-white via-gray-50 to-white">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#4267A1]/20 to-[#5A7BC4]/20 flex items-center justify-center border border-[#4267A1]/30 animate-pulse">
            <Calendar className="w-6 h-6 text-[#4267A1]" />
          </div>
          <p className="text-gray-700 font-medium" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
            Loading beautiful calendar...
          </p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="h-full bg-gradient-to-br from-white via-gray-50 to-white p-6"
      style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
    >
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-4xl font-bold bg-gradient-to-r from-[#4267A1] via-[#5A7BC4] to-[#4267A1] bg-clip-text text-transparent mb-2">
            Work Calendar
          </h2>
          <p className="text-gray-600 text-lg font-medium">Track deadlines and schedule work items</p>
        </div>
        
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-3 bg-gradient-to-r from-gray-100 to-gray-200 hover:from-[#4267A1] hover:to-[#5A7BC4] rounded-xl transition-all duration-300 border border-gray-300 hover:border-[#4267A1]/50"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700 hover:text-white" />
          </button>
          
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-800">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
          </div>
          
          <button
            onClick={() => navigateMonth('next')}
            className="p-3 bg-gradient-to-r from-gray-100 to-gray-200 hover:from-[#4267A1] hover:to-[#5A7BC4] rounded-xl transition-all duration-300 border border-gray-300 hover:border-[#4267A1]/50"
          >
            <ChevronRight className="w-5 h-5 text-gray-700 hover:text-white" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-lg">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-4 mb-4">
          {dayNames.map(day => (
            <div key={day} className="text-center py-3">
              <span className="text-gray-700 font-semibold text-sm tracking-wide">{day}</span>
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-4">
          {getDaysInMonth(currentDate).map((date, index) => {
            const events = getEventsForDate(date);
            const isToday = date && date.toDateString() === new Date().toDateString();
            
            return (
              <div
                key={index}
                className={`min-h-24 p-2 rounded-lg border transition-all duration-300 ${
                  date 
                    ? `bg-gradient-to-br from-gray-50 to-white border-gray-200 hover:border-[#4267A1]/50 cursor-pointer ${
                        isToday ? 'ring-2 ring-[#4267A1]/50' : ''
                      }`
                    : 'border-transparent'
                }`}
              >
                {date && (
                  <>
                    <div className={`text-sm font-medium mb-2 ${
                      isToday ? 'text-[#4267A1]' : 'text-gray-700'
                    }`}>
                      {date.getDate()}
                    </div>
                    
                    <div className="space-y-1">
                      {events.slice(0, 3).map(event => (
                        <motion.div
                          key={event.id}
                          whileHover={{ scale: 1.02 }}
                          onClick={() => setSelectedEvent(event)}
                          className={`p-2 rounded-md bg-gradient-to-r ${event.color} text-white text-xs font-medium cursor-pointer shadow-sm hover:shadow-md transition-all duration-200`}
                        >
                          <div className="truncate">{event.title}</div>
                        </motion.div>
                      ))}
                      {events.length > 3 && (
                        <div className="text-xs text-gray-500 text-center">
                          +{events.length - 3} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setSelectedEvent(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-sm rounded-xl p-6 max-w-md w-full mx-4 border border-slate-700/50 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-xl font-bold text-white">{selectedEvent.title}</h3>
              <button
                onClick={() => setSelectedEvent(null)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-3">
              {selectedEvent.workObject.description && (
                <p className="text-slate-300 text-sm leading-relaxed">
                  {selectedEvent.workObject.description}
                </p>
              )}
              
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Clock className="w-4 h-4" />
                <span>Due: {selectedEvent.date.toLocaleDateString()}</span>
              </div>
              
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r ${selectedEvent.color} text-white text-sm font-medium`}>
                <span>{selectedEvent.workObject.status}</span>
              </div>
              
              {selectedEvent.workObject.assignees.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <User className="w-4 h-4" />
                  <span>{selectedEvent.workObject.assignees.join(', ')}</span>
                </div>
              )}
              
              {selectedEvent.workObject.labels && selectedEvent.workObject.labels.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Tag className="w-4 h-4" />
                  <div className="flex flex-wrap gap-1">
                    {selectedEvent.workObject.labels.map(label => (
                      <span key={label} className="bg-gradient-to-r from-emerald-500/20 to-emerald-600/20 text-emerald-300 px-2 py-1 rounded-full border border-emerald-500/30 text-xs">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};
